import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { recordingsApi } from '../../../lib/api';
import {
  Search,
  Film,
  Play,
  Download,
  Trash2,
  MoreVertical,
  Calendar,
  Clock,
  Users,
  Loader2,
  RefreshCw,
  FileVideo,
  Eye,
  Share2,
} from 'lucide-react';

interface Recording {
  id: string;
  title: string;
  meeting_title: string;
  duration: number;
  file_size: number;
  status: 'processing' | 'ready' | 'failed';
  created_at: string;
  meeting: { id: string; title: string } | null;
  course: { id: string; name: string } | null;
  recorded_by: { id: string; name: string } | null;
}

export function RecordingsPage() {
  const { t } = useTranslation();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [playingRecording, setPlayingRecording] = useState<Recording | null>(null);

  const fetchRecordings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await recordingsApi.list();
      setRecordings(response.recordings);
    } catch (err: unknown) {
      console.error('Failed to fetch recordings:', err);
      setError('Failed to load recordings');
      setRecordings([
        {
          id: '1',
          title: 'Mathematics Lesson - Chapter 5',
          meeting_title: 'Mathematics Advanced - Week 5',
          duration: 3600,
          file_size: 524288000,
          status: 'ready',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          meeting: { id: '1', title: 'Mathematics Advanced - Week 5' },
          course: { id: '1', name: 'Mathematics Advanced' },
          recorded_by: { id: '1', name: 'Sarah Cohen' },
        },
        {
          id: '2',
          title: 'Physics Lab Session',
          meeting_title: 'Physics Lab - Experiment 3',
          duration: 2700,
          file_size: 393216000,
          status: 'ready',
          created_at: new Date(Date.now() - 172800000).toISOString(),
          meeting: { id: '2', title: 'Physics Lab - Experiment 3' },
          course: { id: '2', name: 'Physics Fundamentals' },
          recorded_by: { id: '2', name: 'David Levi' },
        },
        {
          id: '3',
          title: 'Staff Meeting Recording',
          meeting_title: 'Monthly Staff Meeting',
          duration: 0,
          file_size: 0,
          status: 'processing',
          created_at: new Date().toISOString(),
          meeting: { id: '3', title: 'Monthly Staff Meeting' },
          course: null,
          recorded_by: { id: '3', name: 'Admin User' },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordings();
  }, []);

  const filteredRecordings = recordings.filter((recording) => {
    const matchesSearch =
      recording.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recording.meeting_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recording.course?.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || recording.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (recordingId: string) => {
    if (!confirm('Are you sure you want to delete this recording?')) return;
    try {
      await recordingsApi.delete(recordingId);
      fetchRecordings();
    } catch (err) {
      console.error('Failed to delete recording:', err);
    }
    setContextMenuId(null);
  };

  const handleDownload = async (recordingId: string) => {
    try {
      const response = await recordingsApi.getDownloadUrl(recordingId);
      window.open(response.url, '_blank');
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
    setContextMenuId(null);
  };

  const handleShare = (recordingId: string) => {
    navigator.clipboard.writeText(window.location.origin + '/recordings/' + recordingId);
    setContextMenuId(null);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return hours + ':' + String(minutes).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
    }
    return minutes + ':' + String(secs).padStart(2, '0');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: Recording['status']) => {
    const styles: Record<string, string> = {
      processing: 'bg-yellow-100 text-yellow-700',
      ready: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };
    const labels: Record<string, string> = {
      processing: 'Processing',
      ready: 'Ready',
      failed: 'Failed',
    };
    return (
      <span className={'px-2 py-1 text-xs font-medium rounded-full ' + styles[status]}>
        {labels[status]}
      </span>
    );
  };

  const RecordingCard = ({ recording }: { recording: Recording }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-32 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {recording.status === 'ready' ? (
            <button
              onClick={() => setPlayingRecording(recording)}
              className="p-2 bg-teal-600 rounded-full text-white hover:bg-teal-700"
            >
              <Play className="w-5 h-5" />
            </button>
          ) : recording.status === 'processing' ? (
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
          ) : (
            <FileVideo className="w-8 h-8 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900 truncate">{recording.title}</h3>
            {getStatusBadge(recording.status)}
          </div>

          {recording.course && (
            <p className="text-sm text-teal-600 mb-1">{recording.course.name}</p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(recording.created_at)}
            </span>
            {recording.duration > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDuration(recording.duration)}
              </span>
            )}
            {recording.file_size > 0 && (
              <span className="flex items-center gap-1">
                <FileVideo className="w-4 h-4" />
                {formatFileSize(recording.file_size)}
              </span>
            )}
            {recording.recorded_by && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {recording.recorded_by.name}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {recording.status === 'ready' && (
            <>
              <button
                onClick={() => setPlayingRecording(recording)}
                className="px-3 py-1.5 text-sm font-medium text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 flex items-center gap-1"
              >
                <Eye className="w-4 h-4" />
                Watch
              </button>
              <button
                onClick={() => handleDownload(recording.id)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Download className="w-4 h-4" />
              </button>
            </>
          )}

          <div className="relative">
            <button
              onClick={() => setContextMenuId(contextMenuId === recording.id ? null : recording.id)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {contextMenuId === recording.id && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {recording.status === 'ready' && (
                  <>
                    <button
                      onClick={() => {
                        setPlayingRecording(recording);
                        setContextMenuId(null);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Play className="w-4 h-4" />
                      Play
                    </button>
                    <button
                      onClick={() => handleDownload(recording.id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={() => handleShare(recording.id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Share2 className="w-4 h-4" />
                      Share Link
                    </button>
                    <hr className="my-1" />
                  </>
                )}
                <button
                  onClick={() => handleDelete(recording.id)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search recordings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="all">All Status</option>
          <option value="ready">Ready</option>
          <option value="processing">Processing</option>
          <option value="failed">Failed</option>
        </select>

        <button
          onClick={fetchRecordings}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-4">
        {filteredRecordings.length > 0 ? (
          filteredRecordings.map((recording) => (
            <RecordingCard key={recording.id} recording={recording} />
          ))
        ) : (
          <div className="text-center py-12">
            <Film className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No recordings found</p>
            <p className="text-sm text-gray-400 mt-1">
              Recordings will appear here after you record a meeting
            </p>
          </div>
        )}
      </div>

      {playingRecording && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80" onClick={() => setPlayingRecording(null)} />
          <div className="relative w-full max-w-4xl bg-black rounded-xl overflow-hidden">
            <div className="aspect-video flex items-center justify-center">
              <p className="text-white">Video player placeholder - {playingRecording.title}</p>
            </div>
            <div className="p-4 bg-gray-900">
              <h3 className="text-white font-medium">{playingRecording.title}</h3>
              <p className="text-gray-400 text-sm">{playingRecording.course?.name}</p>
            </div>
            <button
              onClick={() => setPlayingRecording(null)}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
